import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
    Box,
    Grid,
    Typography,
    LinearProgress,
    useMediaQuery,
    Button,
    IconButton,
    Collapse,
    Tooltip,
    Popover,
    List,
    ListItemButton,
    ListItemText,
    Divider,
    Paper,
    SvgIcon,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BalanceIcon from '@mui/icons-material/Balance';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
//import InfoOutlinedIcon from '@mui/icons-material/ContactEmergency';
//import FacebookIcon from '@mui/icons-material/Facebook';
//import LinkedInIcon from '@mui/icons-material/LinkedIn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLinkedinIn, faFacebookF } from '@fortawesome/free-brands-svg-icons';

import TwitterIcon from '@mui/icons-material/X';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';

import RadarChart from '../../components/radarChart'; // Asegúrate de que el componente del gráfico esté implementado.
import theme from '../../styles/StyleTheme'; // Importa tu archivo de estilos.
import { useNavigate, useParams } from 'react-router-dom';
import buildingsBackground from '../../assets/images/fondo-edificiospng.png';
import { publicAxios } from '../../apiClients';
import BackButton from '../../components/BackButton';
import { RANKING_TOOLTIPS } from '../../components/rankingTooltips';
import { generateFirmRouteSegments } from '../../utils/slugify';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import PDFComponent from '../../components/PDF_component';
import RankingsTable from '../../components/RankingsTable';
import Loading from '../../components/Loading';
import logoMMWhite from '../../assets/banners/MM-blanco sin fondo.png';

const ShareNodeIcon = (props) => (
    <SvgIcon viewBox="0 0 24 24" {...props}>
        <circle cx="6" cy="12" r="2.8" />
        <circle cx="18" cy="6" r="2.8" />
        <circle cx="18" cy="18" r="2.8" />
        <path d="M8.4 11.1L15.2 7.8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        <path d="M8.4 12.9L15.2 16.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </SvgIcon>
);


const MarketFirm = () => {
    const navigate = useNavigate();
    const { countrySlug, firmSlug } = useParams();
    const [firm, setFirm] = useState(null);
    const [rankings, setRankings] = useState([]);
    const [showRankings, setShowRankings] = useState(false);
    const rankingsRef = useRef(null);
    const [showClients, setShowClients] = useState(false);
    const [clients, setClients] = useState([]);
    const [isLoadingClients, setIsLoadingClients] = useState(false);
    const [clientsError, setClientsError] = useState(null);
    const clientsRef = useRef(null);
    const pdfComponentRef = useRef(null);
    const [visits, setVisits] = useState(0);
    const [error, setError] = useState(null);
    const [firmId, setFirmId] = useState(null);
    const [isResolvingSlug, setIsResolvingSlug] = useState(true);
    const [resolvedSlugs, setResolvedSlugs] = useState({ countrySlug: null, firmSlug: null });
    const [isLoading, setIsLoading] = useState(true);
    const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
    const effectiveFirmSlug = resolvedSlugs.firmSlug ?? firmSlug;
    const effectiveCountrySlug = resolvedSlugs.countrySlug ?? countrySlug;

    // ✅ Ancho consistente para centrar elementos (encabezado, tabla, artículos, etc.)
    const CONTENT_WIDTH = { xs: '100%', sm: '92%', md: '82%', lg: '78%' };

    const [iconOffset, setIconOffset] = useState(-1);

    const [shareAnchorEl, setShareAnchorEl] = useState(null);
    const [currentShareUrl, setCurrentShareUrl] = useState('');
    const [isShareLinkCopied, setIsShareLinkCopied] = useState(false);




    const handleIconOffsetChange = (newOffset) => {
        setIconOffset(newOffset);
    };



    const { leftColumnInfo, rightColumnInfo } = useMemo(() => {
        if (!firm) {
            return { leftColumnInfo: [], rightColumnInfo: [] };
        }

        const details = [
            firm?.Country?.name
                ? { display: `Country: ${firm.Country.name}`, copyValue: firm.Country.name }
                : null,
            firm?.contactDetail?.city
                ? { display: `City: ${firm.contactDetail.city}`, copyValue: firm.contactDetail.city }
                : null,
            firm?.contactDetail?.address
                ? { display: `Address: ${firm.contactDetail.address}`, copyValue: firm.contactDetail.address }
                : null,
            firm?.contactDetail?.email
                ? { display: `E-mail: ${firm.contactDetail.email}`, copyValue: firm.contactDetail.email }
                : null,
            firm?.contactDetail?.phoneNumber
                ? {
                    display: `Phone number: ${firm.contactDetail.phoneNumber}`,
                    copyValue: firm.contactDetail.phoneNumber,
                }
                : null,
            firm?.contactDetail?.website
                ? { display: `Website: ${firm.contactDetail.website}`, copyValue: firm.contactDetail.website }
                : null,
        ].filter(Boolean);

        return {
            leftColumnInfo: details.slice(0, 3),
            rightColumnInfo: details.slice(3, 6),
        };
    }, [firm]);
    const contactDetails = useMemo(
        () => [...leftColumnInfo, ...rightColumnInfo],
        [leftColumnInfo, rightColumnInfo],
    );

    const articles = useMemo(() => {
        if (!firm?.articles || !Array.isArray(firm.articles)) {
            return [];
        }

        return [...firm.articles]
            .filter((article) => Boolean(article?.link))
            .sort((a, b) => {
                if (!a.fecha && !b.fecha) {
                    return 0;
                }
                if (!a.fecha) {
                    return 1;
                }
                if (!b.fecha) {
                    return -1;
                }
                return new Date(b.fecha) - new Date(a.fecha);
            })
            .slice(0, 4);
    }, [firm]);

    const countryImages = useMemo(() => {
        const images = firm?.Country?.images;
        if (!images || !Array.isArray(images)) {
            return [];
        }

        return [...images].sort((a, b) => {
            const priorityA = typeof a.priority === 'number' ? a.priority : Number.MAX_SAFE_INTEGER;
            const priorityB = typeof b.priority === 'number' ? b.priority : Number.MAX_SAFE_INTEGER;
            return priorityA - priorityB;
        });
    }, [firm]);

    const articleGridItems = useMemo(() => {
        const fallbackImage = countryImages.length > 0 ? countryImages[0] : null;

        return articles.map((article, index) => ({
            article,
            image: countryImages[index] ?? fallbackImage,
        }));
    }, [articles, countryImages]);

    const clientColumns = useMemo(() => {
        if (!Array.isArray(clients) || clients.length === 0) {
            return [];
        }

        let columnCount = 1;
        if (clients.length > 14) {
            columnCount = 3;
        } else if (clients.length > 7) {
            columnCount = 2;
        }

        columnCount = Math.min(columnCount, clients.length);

        const baseSize = Math.floor(clients.length / columnCount);
        const remainder = clients.length % columnCount;
        const columns = [];
        let startIndex = 0;

        for (let index = 0; index < columnCount; index += 1) {
            const extraItem = index < remainder ? 1 : 0;
            const columnSize = baseSize + extraItem;
            const endIndex = startIndex + columnSize;
            columns.push(clients.slice(startIndex, endIndex));
            startIndex = endIndex;
        }

        return columns;
    }, [clients]);

    const clientColumnCount = clientColumns.length;

    const clientColumnGridProps = useMemo(() => {
        if (clientColumnCount <= 1) {
            return { xs: 12 };
        }
        if (clientColumnCount === 2) {
            return { xs: 12, sm: 6 };
        }
        return { xs: 12, sm: 6, md: 4 };
    }, [clientColumnCount]);

    // Uso de useMediaQuery para detectar pantallas pequeñas
    const isMobile = useMediaQuery('(max-width:600px)'); // Ajusta el breakpoint según necesites

    const isNarrow = useMediaQuery('(max-width:1270px)');
    const enableScroll = isNarrow && !isMobile;


    useEffect(() => {
        window.scrollTo(0, 0);
        setError(null);
        setFirmId(null);
        setIsResolvingSlug(true);
        setFirm(null);
        setRankings([]);
        setVisits(0);
        setClients([]);
        setShowClients(false);
        setClientsError(null);
        setIsLoadingClients(false);
        setIsLoading(true);

        if (!firmSlug) {
            setError('Firm identifier is missing.');
            setIsResolvingSlug(false);
            setIsLoading(false);
            return;
        }
        const resolveFirmId = async () => {
            try {
                const response = await publicAxios.get('/institution/market-monitor-legal/table');
                let strictMatch = null;
                let strictSegments = null;
                let looseMatch = null;
                let looseSegments = null;

                response.data.forEach((item) => {
                    const segments = generateFirmRouteSegments({
                        institutionName: item.institutionName,
                        countryName: item.Country?.name ?? item.country?.name,
                        countryId: item.Country?.id ?? item.country?.id,
                        firmId: item.id,
                    });

                    if (
                        countrySlug &&
                        !strictMatch &&
                        segments.firmSlug === firmSlug &&
                        segments.countrySlug === countrySlug
                    ) {
                        strictMatch = item;
                        strictSegments = segments;
                    }

                    if (!looseMatch && segments.firmSlug === firmSlug) {
                        looseMatch = item;
                        looseSegments = segments;
                    }
                });
                const matchedFirm = strictMatch ?? looseMatch;
                const matchedSegments = strictSegments ?? looseSegments;


                if (!matchedFirm) {
                    setError('Unable to find the requested firm.');
                    setIsLoading(false);
                    return;
                }
                if (matchedSegments) {
                    setResolvedSlugs({
                        countrySlug: matchedSegments.countrySlug,
                        firmSlug: matchedSegments.firmSlug,
                    });
                } else {
                    setResolvedSlugs({ countrySlug: null, firmSlug: null });
                }

                setFirmId(matchedFirm.id);
            } catch (fetchError) {
                console.error('Error resolving firm slug:', fetchError);
                setError('Unable to load the requested firm.');
                setIsLoading(false);
            } finally {
                setIsResolvingSlug(false);
            }
        };

        resolveFirmId();
    }, [countrySlug, firmSlug]);

    useEffect(() => {
        if (!firmId) {
            return;
        }
        setIsLoading(true);

        const fetchFirm = async () => {
            try {
                const response = await publicAxios.get(`/institution/market-monitor-legal/${firmId}`);
                const firmData = response.data;
                setFirm(firmData);

                const segments = generateFirmRouteSegments({
                    institutionName: firmData?.institutionName,
                    countryName: firmData?.Country?.name ?? firmData?.country?.name,
                    countryId: firmData?.Country?.id ?? firmData?.country?.id,
                    firmId,
                });

                setResolvedSlugs({
                    countrySlug: segments.countrySlug,
                    firmSlug: segments.firmSlug,
                });
            } catch (fetchError) {
                console.error('Error fetching firm data:', fetchError);
                setError('Unable to load the requested firm.');
            } finally {
                setIsLoading(false);
            }
        };
        const fetchRankings = async () => {
            try {
                const response = await publicAxios.get(`/ranking/${firmId}`);
                setRankings(response.data);
            } catch (fetchError) {
                console.error('Error fetching rankings data:', fetchError);
            }
        };
        const fetchClients = async () => {
            setIsLoadingClients(true);
            setClientsError(null);
            try {
                const response = await publicAxios.get(`/institution/market-monitor-legal/${firmId}/clients`);
                if (Array.isArray(response.data)) {
                    setClients(response.data);
                } else {
                    setClients([]);
                }
            } catch (fetchError) {
                console.error('Error fetching clients data:', fetchError);
                setClients([]);
                setClientsError('Unable to load clients.');
            } finally {
                setIsLoadingClients(false);
            }
        };
        const fetchVisits = async () => {
            try {
                const response = await publicAxios.get(`/visits/${firmId}`);
                setVisits(response.data.count);
                return response.data.count;
            } catch (fetchError) {
                console.error('Error fetching visits:', fetchError);
                return null;
            }
        };
        const registerVisit = async () => {
            try {
                await publicAxios.post(`/visits/${firmId}`);
            } catch (fetchError) {
                console.error('Error updating visits:', fetchError);
            } finally {
                await fetchVisits();
            }
        };
        fetchFirm();
        fetchRankings();
        fetchClients();
        fetchVisits();
        registerVisit();
    }, [firmId]);

    const handleClickBalance = () => {
        if (!effectiveFirmSlug) {
            return;
        }

        if (effectiveCountrySlug) {
            navigate(`/market-comparative-legal/${effectiveCountrySlug}/${effectiveFirmSlug}`);
            return;
        }
        navigate(`/market-comparative-legal/${effectiveFirmSlug}`);
    };

    useEffect(() => {
        if (showRankings && rankingsRef.current) {
            const timeout = setTimeout(() => {
                rankingsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [showRankings]);

    useEffect(() => {
        if (showClients && clientsRef.current) {
            const timeout = setTimeout(() => {
                clientsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 300);
            return () => clearTimeout(timeout);
        }
    }, [showClients]);

    // Estados para controlar la expansión de cada box
    const [isExpanded, setIsExpanded] = useState({
        reputation: false,
        revenue: false,
        trajectory: false,
        clients: false,
        handsOnWork: false,
        arena: false,
    });
    const [infoAnchorEl, setInfoAnchorEl] = useState(null);
    const [copiedContactIndex, setCopiedContactIndex] = useState(null);
    const copyTimeoutRef = useRef(null);
    const shareCopyTimeoutRef = useRef(null);

    const isShareOpen = Boolean(shareAnchorEl);

    const handleOpenShare = (event) => {
        setInfoAnchorEl(null);
        if (shareCopyTimeoutRef.current) {
            clearTimeout(shareCopyTimeoutRef.current);
            shareCopyTimeoutRef.current = null;
        }
        setIsShareLinkCopied(false);
        setShareAnchorEl((previousAnchor) => (previousAnchor ? null : event.currentTarget));
    };

    const handleCloseShare = () => {
        setShareAnchorEl(null);
        if (shareCopyTimeoutRef.current) {
            clearTimeout(shareCopyTimeoutRef.current);
            shareCopyTimeoutRef.current = null;
        }
        setIsShareLinkCopied(false);
    };

    const handleOpenInfo = (event) => {
        setShareAnchorEl(null);
        setInfoAnchorEl((previousAnchor) => (previousAnchor ? null : event.currentTarget));
    };

    const handleCloseInfo = () => {
        setInfoAnchorEl(null);
        if (copyTimeoutRef.current) {
            clearTimeout(copyTimeoutRef.current);
            copyTimeoutRef.current = null;
        }
        setCopiedContactIndex(null);
    };

    const isInfoOpen = Boolean(infoAnchorEl);
    const handleCopyContactDetail = async (valueToCopy, index) => {
        try {
            const textToCopy = valueToCopy != null ? String(valueToCopy) : '';
            await navigator.clipboard.writeText(textToCopy);
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }
            setCopiedContactIndex(index);
            copyTimeoutRef.current = setTimeout(() => {
                setCopiedContactIndex(null);
                copyTimeoutRef.current = null;
            }, 2000);
        } catch (errorCopyingContactDetail) {
            console.error('Error copying contact detail:', errorCopyingContactDetail);
        }
    };

    useEffect(() => {
        if (!isInfoOpen && copiedContactIndex !== null) {
            setCopiedContactIndex(null);
        }
    }, [isInfoOpen, copiedContactIndex]);

    useEffect(
        () => () => {
            if (copyTimeoutRef.current) {
                clearTimeout(copyTimeoutRef.current);
            }
        },
        [],
    );

    useEffect(() => {
        if (!effectiveCountrySlug || !effectiveFirmSlug) {
            setCurrentShareUrl('');
            return;
        }

        // 🔥 Usar SIEMPRE el backend para LinkedIn
        const backendShareUrl = `https://backmm.thelegalindustry.com/share/market-firm/${effectiveCountrySlug}/${effectiveFirmSlug}`;

        setCurrentShareUrl(backendShareUrl);
    }, [effectiveCountrySlug, effectiveFirmSlug]);


    useEffect(
        () => () => {
            if (shareCopyTimeoutRef.current) {
                clearTimeout(shareCopyTimeoutRef.current);
                shareCopyTimeoutRef.current = null;
            }
        },
        [],
    );

    const actionButtonSx = {
        color: '#fff',
        padding: 0,
        backgroundColor: 'transparent',
        border: 'none',
        width: 'auto',
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        '&:hover': {
            color: theme.palette.primary.main, // solo cambia el color al pasar el mouse
            transform: 'scale(1.05)',
        },
    };

    const shareOptionButtonSx = {
        width: 48,
        height: 48,
        borderRadius: '50%',
        border: '1px solid rgba(3,38,61,0.18)',
        backgroundColor: '#ffffff',
        color: '#03263D',
        transition: 'all 0.25s ease',
        boxShadow: '0 1px 4px rgba(3,38,61,0.12)',
        '&:hover': {
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
            boxShadow: '0 6px 18px rgba(3,38,61,0.35)',
            transform: 'translateY(-2px)',
        },
    };

    const shareOptions = useMemo(() => {
        if (!currentShareUrl) {
            return [];
        }

        const encodedUrl = encodeURIComponent(currentShareUrl);
        const profileName = firm?.institutionName ?? 'esta firma';

        const locationParts = [firm?.contactDetail?.city, firm?.Country?.name]
            .filter(Boolean)
            .join(', ');
        const contactSegments = [
            firm?.contactDetail?.email ? `Mail: ${firm.contactDetail.email}` : null,
            firm?.contactDetail?.phoneNumber ? `Phone: ${firm.contactDetail.phoneNumber}` : null,
            firm?.contactDetail?.website ? `Web Site: ${firm.contactDetail.website}` : null,
        ].filter(Boolean);

        const shareDescriptionParts = [
            `Discover ${profileName} in Legal Monitor.`,
            locationParts ? `Location: ${locationParts}.` : null,
            contactSegments.length > 0 ? `Contact: ${contactSegments.join(' | ')}.` : null,
        ].filter(Boolean);

        const shareDescription =
            shareDescriptionParts.length > 0
                ? shareDescriptionParts.join(' ')
                : 'Discover this profile in Market Monitor Legal.';
        const shareMessage = [shareDescription, currentShareUrl].filter(Boolean).join(' ');
        const encodedShareDescription = encodeURIComponent(shareDescription);
        const encodedShareMessage = encodeURIComponent(shareMessage);
        const encodedProfileName = encodeURIComponent(profileName);


        return [
            {
                id: 'facebook',
                label: 'Share on Facebook',
                icon: () => <FontAwesomeIcon icon={faFacebookF} style={{ fontSize: 22 }} />,
                url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}&quote=${encodedShareMessage}`,
            },
            {
                id: 'x',
                label: 'Share on X',
                icon: TwitterIcon,
                url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(
                    "On the radar: Legal Monitor puts firms in front of GCs, in-house teams, and legal decision-makers. Check out this profile"
                )
                    }&url=${encodedUrl}`,
            },
            {
                id: 'linkedin',
                label: 'Share on LinkedIn',
                icon: () => <FontAwesomeIcon icon={faLinkedinIn} style={{ fontSize: 22 }} />,
                url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
            },

            {
                id: 'whatsapp',
                label: 'Share on WhatsApp',
                icon: WhatsAppIcon,
                url: `https://api.whatsapp.com/send?text=${encodeURIComponent(
                    "On the radar: Legal Monitor puts firms in front of GCs, in-house teams, and legal decision-makers. Check out this profile"
                ) + "%0A" + encodeURIComponent(currentShareUrl)
                    }`,
            },
            {
                id: 'copy',
                label: 'Copy link',
                icon: ContentCopyIcon,
                action: 'copy',
            },
        ];
    }, [currentShareUrl, firm]);

    const handleShareOptionClick = (option) => {
        if (!option) {
            return;
        }

        if (option.action === 'copy') {
            if (!currentShareUrl) {
                return;
            }

            const handleCopySuccess = () => {
                setIsShareLinkCopied(true);
                if (shareCopyTimeoutRef.current) {
                    clearTimeout(shareCopyTimeoutRef.current);
                }
                shareCopyTimeoutRef.current = setTimeout(() => {
                    setIsShareLinkCopied(false);
                    shareCopyTimeoutRef.current = null;
                }, 2000);
            };

            const fallbackCopy = () => {
                if (typeof document === 'undefined') {
                    return;
                }
                try {
                    const tempTextArea = document.createElement('textarea');
                    tempTextArea.value = currentShareUrl;
                    tempTextArea.setAttribute('readonly', '');
                    tempTextArea.style.position = 'fixed';
                    tempTextArea.style.opacity = '0';
                    document.body.appendChild(tempTextArea);
                    tempTextArea.select();
                    const successful = document.execCommand('copy');
                    document.body.removeChild(tempTextArea);
                    if (successful) {
                        handleCopySuccess();
                    }
                } catch (errorCopyingShareLink) {
                    console.error('Error copying share link:', errorCopyingShareLink);
                }
            };

            if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
                navigator.clipboard
                    .writeText(currentShareUrl)
                    .then(handleCopySuccess)
                    .catch((errorCopyingShareLink) => {
                        console.error('Error copying share link:', errorCopyingShareLink);
                        fallbackCopy();
                    });
            } else {
                fallbackCopy();
            }

            return;
        }

        if (option.url && typeof window !== 'undefined') {
            window.open(option.url, '_blank', 'noopener,noreferrer');
        }
        handleCloseShare();
    };


    // Función para manejar el clic en "Ver más"
    const handleSeeMore = (key) => {
        setIsExpanded((prevState) => ({
            ...prevState,
            [key]: !prevState[key],
        }));
    };

    // Textos para cada box
    const boxTexts = {
        reputation:
            "Firm's performance in major industry rankings, such as Chambers & Partners, The Legal 500, Who is Who Legal, among others.",
        brandPower: "The ability of law firms to inspire trust, stand out in the legal market, and build client loyalty through its reputation, expertise, and values.",
        trajectory: 'Recognizes the time that firm’s key professionals have been operating in the market.',
        clients: 'Highlights the relevant players that trust in the firm.                                                           ',
        handsOnWork:
            "Refers to practical, active involvement in tasks or projects to gain direct experience.",
        arena: 'Measures the market conditions in which the firm operates.                                                          ',
    };

    // Datos para cada box
    const boxes = [
        { key: 'reputation', title: 'Reputation', value: firm?.reputation || 0, gridColumn: '3', gridRow: '2' },
        { key: 'brandPower', title: 'Visibility', value: firm?.brandPower || 0, gridColumn: '4', gridRow: '2' },
        { key: 'trajectory', title: 'Trajectory', value: firm?.trajectory || 0, gridColumn: '1', gridRow: '3' },
        { key: 'clients', title: 'Top Active Clients', value: firm?.clients || 0, gridColumn: '2', gridRow: '3' },
        { key: 'handsOnWork', title: 'Hands-on Work', value: firm?.handsOnWork || 0, gridColumn: '3', gridRow: '3' },
        { key: 'arena', title: 'Arena', value: firm?.arena || 0, gridColumn: '4', gridRow: '3' },
    ];

    const handleDownloadPdf = async () => {
        if (!pdfComponentRef.current || !firm) {
            return;
        }

        try {
            setIsGeneratingPdf(true);
            const element = pdfComponentRef.current;

            await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

            const canvas = await html2canvas(element, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                scrollX: 0,
                scrollY: 0,
                windowWidth: element.scrollWidth,
                windowHeight: element.scrollHeight,
            });

            const imgData = canvas.toDataURL('image/png');
            const pdf = new jsPDF('p', 'pt', 'letter');
            const pageWidth = pdf.internal.pageSize.getWidth();
            const pageHeight = pdf.internal.pageSize.getHeight();
            const imgWidth = pageWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            let heightLeft = imgHeight;
            let position = 0;

            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;

            while (heightLeft > 0) {
                position = heightLeft - imgHeight;
                pdf.addPage();
                pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
                heightLeft -= pageHeight;
            }

            const safeName = firm?.institutionName
                ? firm.institutionName.replace(/[\\/:*?"<>|]+/g, '')
                : 'market-firm';
            pdf.save(`${safeName || 'market-firm'}.pdf`);
        } catch (pdfError) {
            console.error('Error generating PDF:', pdfError);
        } finally {
            setIsGeneratingPdf(false);
        }
    };

    const renderRankingWithTooltip = (value) => {
        if (value === 'NRF' || value === 'NRPA') {
            return (
                <>
                    {value}
                    <Tooltip title={RANKING_TOOLTIPS[value]} placement="top" arrow>
                        <ErrorOutlineIcon fontSize="small" sx={{ ml: 0.5 }} color="warning" />
                    </Tooltip>
                </>
            );
        }
        return value || 'N/A';
    };

    if (isLoading) {
        return (
            <Loading
                background="#03263D"//color fondo  
                barTrackColor="rgba(255, 255, 255, 0.45)"
                barColor="#FFFFFF"
                logo={logoMMWhite}
                logoAlt="Market Monitor"
            />
        );
    }



    if (error && !isResolvingSlug) {
        return (
            <Box sx={{ p: 4 }}>
                <Typography variant="h6" color="error" sx={{ mb: 2 }}>
                    {error}
                </Typography>
                <Button variant="outlined" onClick={() => navigate(-1)}>
                    Go back
                </Button>
            </Box>
        );
    }


    return (
        <>
            <Box
                sx={{
                    position: 'absolute',
                    top: 0,
                    left: '-10000px',
                    pointerEvents: 'none',
                }}
            >
                <PDFComponent
                    ref={pdfComponentRef}
                    firm={firm}
                    boxes={boxes}
                    rankings={rankings}
                    contactDetails={contactDetails}
                />
            </Box>
            <Box
                sx={{
                    backgroundImage: `url(${buildingsBackground})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    paddingX: isMobile ? '0%' : '5%',
                }}
            >
                <Grid container justifyContent="start" sx={{ mt: 1, ml: 0, mb: -3 }}>
                    <BackButton path="/market-monitor-legal" />
                </Grid>
                <Box sx={{ p: 4 }}>
                    {/* Encabezado */}
                    <Box
                        sx={{
                            backgroundColor: '#03263D',
                            borderRadius: 2,
                            boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                            padding: 3,
                            marginBottom: 3,
                        }}
                    >
                        <Grid container spacing={2} alignItems="center">
                            {/* Columna 1: Nombre (sm={4}) */}
                            <Grid item
                                xs={12}
                                sm={4}>
                                {firm && (
                                    <>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <Typography variant="h5" sx={{ color: 'white', fontWeight: 'bold' }}>
                                                {firm.institutionName}
                                            </Typography>
                                        </Box>

                                        <Typography variant="subtitle1" sx={{ color: 'white' }}>
                                            {firm.Country.name}
                                        </Typography>

                                        {/* SEGUNDA FILA: Botón de Comparación */}
                                        {/* ACCIONES: comparar + PDF */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                gap: 1,
                                                flexWrap: { xs: 'wrap', sm: 'nowrap' },
                                                mt: 2,
                                                alignItems: 'stretch', // fuerza igual altura
                                            }}
                                        >
                                            <Button
                                                variant="contained"
                                                onClick={handleClickBalance}
                                                disabled={isResolvingSlug || !effectiveFirmSlug}
                                                sx={{
                                                    backgroundColor: '#3592D2',
                                                    color: '#fff',
                                                    '&:hover': { backgroundColor: '#2878B5' },
                                                    width: { xs: '100%', sm: 'auto' },
                                                    paddingX: 2,
                                                    fontSize: '0.7rem',
                                                    minHeight: '38px', // igual altura
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                Compare with another firm
                                            </Button>

                                            {/* <Button
                                                variant="outlined"
                                                onClick={handleDownloadPdf}
                                                disabled={isGeneratingPdf || isResolvingSlug || !firm}
                                                sx={{
                                                    borderColor: theme.palette.primary.light,
                                                    color: theme.palette.primary.light,
                                                    '&:hover': {
                                                        backgroundColor: '#f67147', // naranjo (ajusta si tu tono es otro)
                                                        borderColor: '#f67147',     // borde blanco
                                                        color: '#FFFFFF',           // texto blanco
                                                    },
                                                    width: { xs: '100%', sm: 'auto' },
                                                    paddingX: 2,
                                                    fontSize: '0.7rem',
                                                    minHeight: '38px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    transition: 'all 0.2s ease-in-out', // suaviza el cambio
                                                }}
                                            >
                                                {isGeneratingPdf ? 'Preparing PDF…' : 'Download as PDF'}
                                            </Button> */}

                                        </Box>

                                        {/* <Button
                                            variant="contained"
                                            onClick={handleClickBalance}
                                            disabled={isResolvingSlug || !effectiveFirmSlug}
                                            sx={{
                                                backgroundColor: '#3592D2',
                                                color: '#fff',
                                                '&:hover': { backgroundColor: '#2878B5' },
                                                width: { xs: '100%', sm: 'auto' },
                                                paddingX: 2,
                                                marginTop: 2,
                                                fontSize: '0.7rem',
                                            }}
                                        >
                                            Compare with another firm
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            onClick={handleDownloadPdf}
                                            disabled={isGeneratingPdf || isResolvingSlug || !firm}
                                            sx={{
                                                borderColor: theme.palette.primary.light,
                                                color: theme.palette.primary.light,
                                                '&:hover': {
                                                    borderColor: theme.palette.primary.main,
                                                    backgroundColor: alpha('#3592D2', 0.08),
                                                },
                                                width: { xs: '100%', sm: 'auto' },
                                                paddingX: 2,
                                                marginTop: 1.5,
                                                fontSize: '0.7rem',
                                            }}
                                        >
                                            {isGeneratingPdf ? 'Preparing PDF…' : 'Download as PDF'}
                                        </Button> */}
                                    </>
                                )}
                            </Grid>

                            {/* --- Columna 2: CONTENEDOR DERECHO ÚNICO (sm={8}) --- */}
                            <Grid
                                item
                                xs={12}
                                sm={8}
                                sx={{
                                    mt: { xs: 2, sm: 0 },
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'center', sm: 'center' },
                                    justifyContent: { xs: 'center', sm: 'flex-end' },
                                    gap: 2.2, // 🔹 reducido de 4 → 2.2 para acercar los bloques
                                }}
                            >

                                {/* Sub-bloque 1: Score/Positions - MOVIDO MÁS A LA DERECHA */}
                                <Box
                                    sx={{
                                        textAlign: { xs: 'center', sm: 'right' },
                                        minWidth: '120px', // 🔹 más compacto
                                        mt: { xs: 2, sm: 0 },
                                        mr: 0,             // 🔹 quitamos margen derecho innecesario
                                    }}
                                >

                                    <Typography variant="body1" sx={{ color: 'white' }}>
                                        Local Position:{' '}
                                        <span
                                            style={{
                                                color: theme.palette.primary.main,
                                                display: 'inline-block',
                                                minWidth: '50px',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {firm?.localPosition || 'N/A'}
                                        </span>
                                    </Typography>
                                    <Typography variant="body1" sx={{ color: 'white' }}>
                                        Global Position:{' '}
                                        <span
                                            style={{
                                                color: theme.palette.primary.main,
                                                display: 'inline-block',
                                                minWidth: '50px',
                                                textAlign: 'center',
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            {firm?.globalPosition || 'N/A'}
                                        </span>
                                    </Typography>
                                </Box>

                                {/* Sub-bloque 2: Score, Visitas e Información */}
                                <Box
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: { xs: 'center', sm: 'flex-end' },
                                        gap: 0.7,         // 🔹 menos separación vertical entre score y visitas
                                        minWidth: '150px', // 🔹 más coherente con el bloque anterior
                                    }}
                                >

                                    {/* Fila de Score - CON MEJOR ESPACIADO */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: { xs: 'center', sm: 'flex-end' },
                                            width: '100%',
                                        }}
                                    >
                                        <Typography
                                            variant="body1"
                                            sx={{
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5, // Agregué gap entre "Score" y ":"
                                            }}
                                        >
                                            <strong style={{ fontSize: '1.4rem' }}>Score</strong>:
                                            <span
                                                style={{
                                                    color: theme.palette.primary.main,
                                                    display: 'inline-block',
                                                    minWidth: '60px',
                                                    textAlign: 'center',
                                                    fontWeight: 'bold',
                                                    fontSize: '1.3rem',
                                                    marginLeft: '4px', // Espacio adicional después de los dos puntos
                                                }}
                                            >
                                                {firm?.totalScore ? firm.totalScore.toFixed(2) : 'N/A'}
                                            </span>
                                        </Typography>
                                    </Box>

                                    {/* Fila de Visitas e Información - ahora juntos */}
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 2,
                                            justifyContent: { xs: 'center', sm: 'flex-end' },
                                            width: '100%',
                                            height: '40px',
                                        }}
                                    >
                                        {/* Contador de Visitas */}
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1,
                                                height: '100%',
                                            }}
                                        >
                                            <VisibilityIcon
                                                sx={{
                                                    color: 'white',
                                                    fontSize: 30,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    transition: 'transform 0.1s ease-in-out',
                                                    '&:active': {
                                                        transform: 'scaleY(0.1)',
                                                    },
                                                }}
                                            />

                                            <Typography
                                                sx={{
                                                    color: 'white',
                                                    fontSize: '1.3rem',
                                                    fontWeight: 'bold',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    height: '100%',
                                                }}
                                            >
                                                {visits}
                                            </Typography>
                                        </Box>

                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.2,
                                            }}
                                        >
                                            {/* boton de compartir */}

                                            <Tooltip title="Share profile" placement="top" arrow>
                                                <IconButton
                                                    onClick={handleOpenShare}
                                                    sx={{
                                                        ...actionButtonSx,
                                                    }}
                                                >
                                                    <ShareNodeIcon sx={{ fontSize: 22 }} />
                                                </IconButton>
                                            </Tooltip>

                                            {/* Tooltip con InfoIcon - ahora junto al ojo y visitas */}
                                            <Tooltip title="Contact information" placement="top" arrow>
                                                <IconButton
                                                    onClick={handleOpenInfo}
                                                    sx={{
                                                        ...actionButtonSx,
                                                        '&:hover': {
                                                            ...actionButtonSx['&:hover'],
                                                            filter: 'drop-shadow(0 0 5px #1e6092ff)',
                                                        },
                                                    }}
                                                >
                                                    <InfoOutlinedIcon
                                                        sx={{
                                                            fontSize: '28px',
                                                            transform: `translateY(${iconOffset}px)`, // Desplaza el icono verticalmente
                                                        }}
                                                    />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Box>
                            </Grid>
                        </Grid>

                        {/* Popover permanece igual */}
                        <Popover
                            open={isInfoOpen}
                            anchorEl={infoAnchorEl}
                            onClose={handleCloseInfo}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            PaperProps={{
                                sx: {
                                    p: 1,
                                    minWidth: 240,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.70),
                                    backdropFilter: 'blur(4px)',
                                },
                            }}
                        >
                            <Box sx={{ p: 1 }}>
                                <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Contact information
                                </Typography>
                                <Divider sx={{ mb: 1 }} />
                                {contactDetails.length > 0 ? (
                                    <List dense disablePadding>
                                        {contactDetails.map((detail, index) => (
                                            <ListItemButton
                                                key={`${detail.display}-${index}`}
                                                onClick={() =>
                                                    handleCopyContactDetail(
                                                        detail.copyValue ?? detail.display,
                                                        index,
                                                    )
                                                }
                                                sx={{
                                                    borderRadius: 1,
                                                    mb: index === contactDetails.length - 1 ? 0 : 0.5,
                                                    bgcolor:
                                                        copiedContactIndex === index
                                                            ? theme.palette.action.selected
                                                            : 'transparent',
                                                }}
                                            >
                                                <ListItemText
                                                    primary={detail.display}
                                                    primaryTypographyProps={{
                                                        sx: {
                                                            fontSize: '0.85rem',
                                                            color:
                                                                copiedContactIndex === index
                                                                    ? theme.palette.primary.main
                                                                    : theme.palette.text.primary,
                                                        },
                                                    }}
                                                />
                                                {copiedContactIndex === index && (
                                                    <Typography variant="caption" color="primary">
                                                        Copied
                                                    </Typography>
                                                )}
                                            </ListItemButton>
                                        ))}
                                    </List>
                                ) : (
                                    <Typography variant="body2" color="text.secondary">
                                        No contact information available.
                                    </Typography>
                                )}
                                {copiedContactIndex !== null && (
                                    <Typography
                                        variant="caption"
                                        sx={{ mt: 1, display: 'block' }}
                                        color="text.secondary"
                                    >
                                        Tap another field to copy it.
                                    </Typography>
                                )}
                            </Box>
                        </Popover>
                        <Popover
                            open={isShareOpen}
                            anchorEl={shareAnchorEl}
                            onClose={handleCloseShare}
                            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                            transformOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                            PaperProps={{
                                sx: {
                                    p: 2,
                                    minWidth: 260,
                                    backgroundColor: alpha(theme.palette.background.paper, 0.7),
                                    backdropFilter: 'blur(4px)',
                                },
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="subtitle1"
                                    sx={{ fontWeight: 'bold', mb: 1, color: '#03263D' }}
                                >
                                    Share this profile on social media
                                </Typography>
                                <Divider sx={{ mb: 1.5 }} />
                                <Box
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                        gap: 1,
                                    }}
                                >
                                    {shareOptions.length > 0 ? (
                                        shareOptions.map((option) => {
                                            const IconComponent = option.icon;
                                            const isCopyAction = option.action === 'copy';
                                            const isDisabled = isCopyAction ? !currentShareUrl : !option.url;
                                            const tooltipTitle =
                                                isCopyAction && isShareLinkCopied ? 'Link copied' : option.label;
                                            return (
                                                <Tooltip key={option.id} title={tooltipTitle} placement="top" arrow>
                                                    <span>
                                                        <IconButton
                                                            onClick={() => handleShareOptionClick(option)}
                                                            disabled={isDisabled}
                                                            sx={shareOptionButtonSx}
                                                        >
                                                            {/* Para FA (fb/linkedin) devolvemos JSX ya con tamaño; 
              para MUI (x/whatsapp/copy) aplicamos sx */}
                                                            {option.id === 'facebook' || option.id === 'linkedin'
                                                                ? <IconComponent />
                                                                : <IconComponent sx={{ fontSize: 26 }} />
                                                            }
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            );
                                        })
                                    ) : (
                                        <Typography variant="body2" color="text.secondary">
                                            No share options available at the moment.
                                        </Typography>
                                    )}
                                </Box>
                                {isShareLinkCopied && (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            mt: 1.5,
                                            textAlign: 'center',
                                            color: theme.palette.success.main,
                                            fontWeight: 500,
                                        }}
                                    >
                                        ¡Link copied to clipboard!
                                    </Typography>
                                )}
                            </Box>
                        </Popover>
                    </Box>

                    {/* Grilla principal */}
                    <Grid
                        container
                        spacing={2}
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: isMobile ? '1fr' : 'repeat(4, 1fr)',
                            gridTemplateRows: 'auto auto auto',
                        }}
                    >
                        {/* Gráfico que ocupa dos columnas y dos filas */}
                        <Grid
                            item
                            sx={{
                                gridColumn: isMobile ? '1' : '1 / span 2',
                                gridRow: isMobile ? '1' : '1 / span 2',
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    backgroundColor: '#03263D',
                                    borderRadius: 2,
                                    boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                                    padding: isMobile ? '0px' : '20px',
                                    height: '100%',
                                    minHeight: '400px',
                                }}
                            >
                                <RadarChart firm={firm} /> {/* Componente del gráfico */}

                                {/* Botón con icono de balanza en la esquina inferior derecha */}
                                {/* <IconButton
                                    sx={{
                                        position: 'absolute',
                                        bottom: 16,
                                        right: 16,
                                        backgroundColor: '#75838E',
                                        color: theme.palette.white,
                                        borderRadius: '12px',
                                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.2)',
                                        width: 48,
                                        height: 48,
                                        '&:hover': {
                                            backgroundColor: '#5A6777',
                                        },
                                    }}
                                    onClick={handleClickBalance}
                                >
                                    <BalanceIcon />
                                </IconButton> */}
                            </Box>
                        </Grid>

                        {/* Boxes en la primera fila */}
                        <Grid item sx={{ gridColumn: isMobile ? '1' : '3', gridRow: isMobile ? 'auto' : '1' }}>
                            {/* Box de Partners */}
                            <Box
                                sx={{
                                    backgroundColor: '#03263D',
                                    padding: 2,
                                    borderRadius: 2,
                                    textAlign: 'center',
                                    minHeight: '190px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="h3" sx={{ color: '#3592D2', fontSize: '3rem' }}>
                                    {firm?.partnersNumber || 'N/A'}
                                </Typography>
                                <Typography variant="h7" sx={{ fontWeight: 'bold', marginBottom: 3, color: 'white' }}>
                                    Partners
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item sx={{ gridColumn: isMobile ? '1' : '4', gridRow: isMobile ? 'auto' : '1' }}>
                            {/* Box de Other qualified members */}
                            <Box
                                sx={{
                                    backgroundColor: '#03263D',
                                    padding: 2,
                                    borderRadius: 2,
                                    textAlign: 'center',
                                    minHeight: '190px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                }}
                            >
                                <Typography variant="h3" sx={{ color: '#3592D2', fontSize: '3rem' }}>
                                    {firm?.otherLawyersNumber || 'N/A'}
                                </Typography>
                                <Typography variant="h7" sx={{ fontWeight: 'bold', marginTop: 0.5, color: 'white' }}>
                                    Other qualified
                                    <br />
                                    members
                                </Typography>
                            </Box>
                        </Grid>

                        {/* Boxes con botón "Ver más" */}
                        {boxes.map((box) => (
                            <Grid
                                key={box.key}
                                item
                                sx={{
                                    gridColumn: isMobile ? '1' : box.gridColumn,
                                    gridRow: isMobile ? 'auto' : box.gridRow,
                                }}
                            >
                                <Box
                                    sx={{
                                        backgroundColor: '#03263D',
                                        padding: 2,
                                        borderRadius: 2,
                                        textAlign: 'center',
                                        minHeight: '190px',
                                        maxHeight: '190px',
                                        overflow: 'hidden',
                                        position: 'relative',
                                    }}
                                >
                                    {/* Contenido del box */}
                                    <Box sx={{ position: 'absolute', top: 10, left: 0, right: 0 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                            <Typography
                                                variant="h7"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    fontSize: isExpanded[box.key] ? '0.9rem' : '1rem',
                                                    transition: 'font-size 0.3s ease',
                                                    color: 'white',
                                                }}
                                            >
                                                {box.title}
                                            </Typography>
                                        </Box>
                                        <Typography
                                            variant="h3"
                                            sx={{
                                                color: '#3592D2',
                                                marginY: 0.8,
                                                fontSize: isExpanded[box.key] ? '1.5rem' : '2rem',
                                                transition: 'font-size 0.3s ease',
                                            }}
                                        >
                                            {box.value || 'N/A'}
                                        </Typography>
                                        {!isExpanded[box.key] && (
                                            <Box sx={{ width: '70%', margin: '0 auto', marginTop: 1.5 }}>
                                                <LinearProgress
                                                    variant="determinate"
                                                    value={box.value || 0} // Default to 0 if box.value is undefined
                                                    sx={{
                                                        height: 10,
                                                        borderRadius: 5,
                                                        backgroundColor: '#DBEAF6',
                                                        '& .MuiLinearProgress-bar': { backgroundColor: '#3592D2' },
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Box>

                                    {/* Botón "Ver más" */}
                                    <Button
                                        variant="text"
                                        sx={{
                                            position: 'absolute',
                                            bottom: isExpanded[box.key] ? '80px' : '10px',
                                            left: 0,
                                            right: 0,
                                            margin: '0 auto',
                                            textTransform: 'none',
                                            color: 'white',
                                            fontSize: '0.8rem',
                                        }}
                                        onClick={() => handleSeeMore(box.key)}
                                    >
                                        {isExpanded[box.key] ? 'See less' : 'See more'}
                                    </Button>

                                    {/* Texto adicional */}
                                    {isExpanded[box.key] && (
                                        <Box
                                            sx={{
                                                position: 'absolute',
                                                left: 10,
                                                right: 10,

                                                // Cuando hay scroll: usamos top+bottom para definir un contenedor con altura fija
                                                // Cuando no, conservamos tu centrado original
                                                top: enableScroll ? '52%' : '60%',
                                                bottom: enableScroll ? 10 : 'auto',
                                                transform: enableScroll ? 'none' : 'translateY(-10%)',

                                                // Scroll solo en pantallas estrechas (no móvil)
                                                overflowY: enableScroll ? 'auto' : 'visible',
                                                pr: enableScroll ? 1 : 0,
                                                WebkitOverflowScrolling: 'touch',

                                                // (Opcional) estilizar scrollbars
                                                scrollbarWidth: enableScroll ? 'thin' : undefined,
                                                '&::-webkit-scrollbar': enableScroll ? { width: 6 } : undefined,
                                                '&::-webkit-scrollbar-thumb': enableScroll
                                                    ? { borderRadius: 6, backgroundColor: 'rgba(255,255,255,0.35)' }
                                                    : undefined,
                                            }}
                                        >
                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    fontSize: '0.8rem',
                                                    color: 'white',
                                                }}
                                            >
                                                {boxTexts[box.key]}
                                            </Typography>
                                        </Box>
                                    )}

                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* {(isLoadingClients || clientsError || clientColumns.length > 0) && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: '#3592D2',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: '#2878B5' },
                                    width: 'auto',
                                    minWidth: '220px',
                                    fontSize: { xs: '0.7rem', md: '0.875rem' },
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    padding: '8px 16px',
                                }}
                                onClick={() => setShowClients((prev) => !prev)}
                            >
                                {showClients ? 'Hide firm clients' : 'See firm clients'}
                            </Button>
                        </Box>

                        <Collapse in={showClients} timeout="auto" unmountOnExit>
                            <Box
                                ref={clientsRef}
                                sx={{
                                    mt: 2,
                                    width: CONTENT_WIDTH,
                                    mx: 'auto',
                                }}
                            >
                                <Paper
                                    sx={{
                                        backgroundColor: '#03263D',
                                        borderRadius: 2,
                                        boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
                                        p: { xs: 2, md: 3 },
                                    }}
                                >
                                    {isLoadingClients ? (
                                        <Box sx={{ width: '100%' }}>
                                            <LinearProgress sx={{ mt: 1 }} color="primary" />
                                        </Box>
                                    ) : clientsError ? (
                                        <Typography sx={{ color: '#fff', textAlign: 'center' }}>
                                            {clientsError}
                                        </Typography>
                                    ) : clientColumns.length === 0 ? (
                                        <Typography sx={{ color: '#fff', textAlign: 'center' }}>
                                            No clients available.
                                        </Typography>
                                    ) : (
                                        <Grid container spacing={2}>
                                            {clientColumns.map((columnClients, columnIndex) => (
                                                <Grid
                                                    item
                                                    key={`client-column-${columnIndex}`}
                                                    {...clientColumnGridProps}
                                                >
                                                    <Box
                                                        component="ul"
                                                        sx={{ listStyle: 'none', p: 0, m: 0 }}
                                                    >
                                                        {columnClients.map((client, clientIndex) => {
                                                            const clientLabel =
                                                                typeof client === 'string'
                                                                    ? client
                                                                    : client?.clientName ?? client?.name ?? '';
                                                            const safeLabel =
                                                                clientLabel && clientLabel.trim().length > 0
                                                                    ? clientLabel
                                                                    : 'Unnamed client';
                                                            const key =
                                                                client?.id ?? `${safeLabel}-${clientIndex}`;

                                                            return (
                                                                <Box
                                                                    component="li"
                                                                    key={key}
                                                                    sx={{
                                                                        color: '#fff',
                                                                        fontSize: '0.95rem',
                                                                        py: 0.5,
                                                                    }}
                                                                >
                                                                    {safeLabel}
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    )}
                                </Paper>
                            </Box>
                        </Collapse>
                    </>
                )} */}

                {/* boton see moire+Ranking table */}
                {rankings.length > 0 && (
                    <>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                            <Button
                                variant="contained"
                                sx={{
                                    backgroundColor: '#3592D2',
                                    color: '#fff',
                                    '&:hover': { backgroundColor: '#2878B5' },
                                    width: 'auto',
                                    minWidth: '220px',
                                    fontSize: { xs: '0.7rem', md: '0.875rem' },
                                    fontWeight: 'bold',
                                    textTransform: 'none',
                                    padding: '8px 16px',
                                }}
                                onClick={() => setShowRankings((prev) => !prev)}
                            >
                                {showRankings ? 'See Less' : 'See the ranked areas here'}
                            </Button>
                        </Box>

                        <Collapse in={showRankings} timeout="auto" unmountOnExit>
                            <Box
                                ref={rankingsRef}
                                sx={{
                                    mt: 2,
                                    width: CONTENT_WIDTH,
                                    mx: 'auto',
                                }}
                            >
                                <RankingsTable
                                    rankings={rankings}
                                    renderValue={renderRankingWithTooltip}
                                    outerBoxSx={{ mt: 0 }}
                                />
                            </Box>
                        </Collapse>
                    </>
                )}




                {/* Grid de 4 artículos centrado */}
                {articleGridItems.length > 0 && (
                    <Box
                        sx={{
                            width: CONTENT_WIDTH,
                            mx: 'auto',
                            mt: 3,
                            p: { xs: '1rem', md: '20px' }
                        }}
                    >
                        <Grid
                            container
                            spacing={3}
                            sx={{
                                justifyContent: { xs: 'center', sm: 'center', md: 'flex-start' },
                            }}
                        >
                            {articleGridItems.map(({ article, image }, index) => (
                                <Grid
                                    item
                                    xs={12}        // 1 columna en móvil
                                    sm={6}         // 2 columnas en tablet pequeña (≥600px)
                                    md={6}         // 2 columnas en tablet (≥900px)  
                                    lg={3}         // 4 columnas en desktop (≥1200px)
                                    key={article.id ?? index}
                                    sx={{
                                        display: 'flex',
                                        justifyContent: 'center'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            backgroundColor: '#03263D',
                                            backgroundImage: image ? `url(${image.imageUrl})` : 'none',
                                            backgroundSize: 'cover',
                                            backgroundPosition: 'center',
                                            p: 3,
                                            borderRadius: 2,
                                            textAlign: 'center',
                                            minHeight: '380px',    // Más alto
                                            height: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'center',
                                            alignItems: 'center',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            width: '100%',
                                            maxWidth: {          // Control del ancho máximo
                                                xs: '320px',      // Móvil
                                                sm: '280px',      // Tablet pequeña  
                                                md: '260px',      // Tablet
                                                lg: '100%'        // Desktop - ocupa todo el espacio disponible
                                            },
                                            aspectRatio: '3/4',  // Relación rectangular (más alto que ancho)
                                            transition: 'transform 0.3s ease-in-out, background-position 0.3s ease-in-out',
                                            '&:hover': {
                                                transform: 'scale(1.02)', // Zoom muy leve del 2%
                                                backgroundPosition: 'center 45%' // Pequeño ajuste en la posición para mejor efecto
                                            }

                                        }}
                                    >
                                        {image && (
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    backgroundColor: 'rgba(3, 38, 61, 0.65)',
                                                }}
                                            />
                                        )}

                                        <Box
                                            sx={{
                                                position: 'relative',
                                                zIndex: 1,
                                                display: 'flex',
                                                flexDirection: 'column',
                                                gap: 2,
                                                alignItems: 'center',
                                                width: '100%',
                                                px: 2
                                            }}
                                        >
                                            <Typography
                                                variant="subtitle1"
                                                sx={{
                                                    fontWeight: 'bold',
                                                    color: 'white',
                                                    textTransform: 'capitalize',
                                                    fontSize: { xs: '1rem', md: '1.1rem' },
                                                    lineHeight: 1.3
                                                }}
                                            >
                                                {/* nuevo campo - codigo */}
                                                {article.codigo || `Artículo ${index + 1}`}
                                                {/* nuevo campo - codigo */}
                                            </Typography>

                                            {article.year && (
                                                <Typography
                                                    variant="body2"
                                                    sx={{ color: '#F0F0F0', fontSize: { xs: '0.95rem', md: '1rem' } }}
                                                >
                                                    {/* nuevo campo - year */}
                                                    {article.year}
                                                    {/* nuevo campo - year */}
                                                </Typography>
                                            )}
                                            {article.area && (
                                                <Typography variant="body2" sx={{ color: '#E0E0E0', fontSize: '0.9rem' }}>
                                                    {/* nuevo campo - area */}
                                                    {article.area}
                                                    {/* nuevo campo - area */}
                                                </Typography>
                                            )}

                                            <Button
                                                component="a"
                                                href={article.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                variant="contained"
                                                sx={{
                                                    backgroundColor: '#3592D2',
                                                    color: '#fff',
                                                    borderRadius: 4,
                                                    height: 40,
                                                    px: 4,
                                                    textTransform: 'none',
                                                    fontSize: '0.9rem',
                                                    mt: 1,
                                                    '&:hover': { backgroundColor: '#2878B5' },
                                                }}
                                            >
                                                Read
                                            </Button>
                                        </Box>
                                    </Box>
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                )}



                {/* Contacto */}
                <Box
                    sx={{
                        backgroundColor: 'none',
                        paddingTop: 4,
                        paddingBottom: 13,
                        paddingX: 8,
                        borderRadius: 'none',
                        display: 'flex',
                        flexDirection: isMobile ? 'column' : 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center', // Esto ya está bien para alinear en el centro vertical
                        minHeight: '80px', // Altura mínima para asegurar espacio
                    }}
                >
                    <Box
                        sx={{
                            flexGrow: 1,
                            textAlign: isMobile ? 'center' : 'left',
                            display: 'flex',
                            alignItems: 'center', // Centra el texto verticalmente
                            height: '100%', // Ocupa toda la altura disponible
                        }}
                    >
                        <Typography
                            variant="body1"
                            sx={{
                                color: 'white',
                                marginBottom: 0, // Eliminamos el margin bottom que separaba
                                display: 'flex',
                                alignItems: 'center', // Centra el texto verticalmente
                                lineHeight: 1.4, // Mejor control del interlineado
                            }}
                        >
                            Our AI model is fed with information coming from public sources or provided by the firms (in this case, verified by our team). Are you a member in this Firm and want to update displayed information?
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            ml: isMobile ? 0 : 4,
                            mt: isMobile ? 2 : 0,
                            display: 'flex',
                            alignItems: 'center', // Centra el botón verticalmente
                            height: isMobile ? 'auto' : '100%', // En móvil altura automática, en desktop toda la altura
                            minHeight: isMobile ? '40px' : 'auto', // Altura mínima en móvil
                        }}
                    >
                        <Button
                            component="a"
                            href="https://thelegalindustry.com/contact-us/"
                            target="_blank"
                            rel="noopener noreferrer"
                            variant="contained"
                            sx={{
                                backgroundColor: '#3592D2',
                                color: '#fff',
                                borderRadius: 4,
                                width: '100%',
                                height: 40, // Aumentamos la altura para mejor alineación
                                textTransform: 'none',
                                whiteSpace: 'nowrap',
                                display: 'flex',
                                alignItems: 'center', // Centra el texto del botón verticalmente
                                justifyContent: 'center',
                                '&:hover': { backgroundColor: '#2878B5' },
                            }}
                        >
                            Contact us
                        </Button>
                    </Box>
                </Box>
            </Box>
        </>
    );
};

export default MarketFirm;
